import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import { useIsManagerView } from '@plato/app-core/hooks/use-view-mode';
import { CrmTaskStatus } from '@plato/data/prisma';
import { endOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { CrmTaskFilterOptions } from '../../../server/dto/crm-task-dto.ts';
import { api } from '../../../utils/api.ts';
import type { MenuGroup } from '../contracts.ts';
import { NavPathOverrideEnum } from '../contracts.ts';
import { buildManagerNavigation } from '../utils/build-manager-navigation.ts';
import { buildRepNavigation } from '../utils/build-rep-navigation.ts';

export function usePlatoLayoutNavigation() {
    const user = useUser();
    const { t } = useTranslation();
    const location = useLocation();

    const isManagerView = useIsManagerView();
    const appConfig = useAppConfig();

    const { data: taskCount } = api.platoUser.query.tasks.getManyTasks.count.useQuery(
        {
            customerIds: [],
            assignedToIds: [user.id],
            assignedByIds: [],
            status: CrmTaskStatus.OPEN,
            dueDate: [
                {
                    direction: 'LT',
                    timestamp: endOfDay(new Date()).toISOString(),
                },
            ],
        } satisfies CrmTaskFilterOptions,
        {
            refetchInterval: 60 * 1000,
        },
    );

    const navigation: MenuGroup[] = useMemo(() => {
        const buildOptions = { t, taskCount, appConfig };
        return isManagerView ? buildManagerNavigation(buildOptions) : buildRepNavigation(buildOptions);
    }, [t, taskCount, appConfig, isManagerView]);

    const activeSection = useMemo(() => {
        // Special case: /integrations/tapi routes should highlight Customers
        if (location.pathname.startsWith('/integrations/tapi')) {
            return NavPathOverrideEnum.CUSTOMERS;
        }

        const items = navigation.flatMap((group) => group.items);

        for (const item of items) {
            if (item.subItems) {
                for (const subItem of item.subItems) {
                    if (location.pathname.startsWith(subItem.href)) {
                        return subItem.id;
                    }
                }
            }
        }

        for (const item of items) {
            if (item.href && location.pathname.startsWith(item.href)) {
                return item.id;
            }
        }

        return NavPathOverrideEnum.HOME;
    }, [navigation, location.pathname]);

    return { navigation, activeSection };
}
